// src/modules/accounting/pages/CasePnlPage.jsx
import React, { useEffect, useState } from 'react';
import {
  Button, Tag, Row, Col, Card, Statistic, Select, Modal, Form, Space,
  message, Typography
} from 'antd';
import {
  ReloadOutlined, CheckCircleOutlined, DollarOutlined,
  ArrowUpOutlined, ArrowDownOutlined, SwapOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getCasePnlEntries, allocateCasePnl, fetchFunds } from '../api';
import DataTable from '../../../components/UI/DataTable';

const { Text } = Typography;

const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const STATUS_COLORS = { PENDING: 'orange', ALLOCATED: 'green' };
const CASE_TYPE_COLORS = { WALK_IN: 'blue', PLEDGE: 'purple' };

export default function CasePnlPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  const [caseTypeFilter, setCaseTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [funds, setFunds] = useState([]);
  const [allocateOpen, setAllocateOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [allocating, setAllocating] = useState(false);
  const [allocateForm] = Form.useForm();
  const [selectedFundId, setSelectedFundId] = useState(null);

  useEffect(() => { load(); loadFunds(); }, []);
  useEffect(() => { load(); }, [pagination.current, pagination.pageSize, caseTypeFilter, statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getCasePnlEntries({
        page: pagination.current,
        limit: pagination.pageSize,
        ...(caseTypeFilter ? { caseType: caseTypeFilter } : {}),
        ...(statusFilter ? { allocationStatus: statusFilter } : {}),
      });
      setData(res.data || []);
      setSummary(res.summary || {});
      setPagination((p) => ({ ...p, total: res.meta?.total || 0 }));
    } catch {
      message.error('Failed to load case P/L entries');
    } finally {
      setLoading(false);
    }
  };

  const loadFunds = async () => {
    try {
      const res = await fetchFunds();
      setFunds(res.data || []);
    } catch { /* non-critical */ }
  };

  const openAllocate = (entry) => {
    setSelectedEntry(entry);
    setSelectedFundId(null);
    allocateForm.resetFields();
    setAllocateOpen(true);
  };

  const handleAllocate = async (values) => {
    if (!selectedEntry) return;
    setAllocating(true);
    try {
      await allocateCasePnl(selectedEntry._id, values);
      message.success('Case P/L allocated to fund');
      setAllocateOpen(false);
      allocateForm.resetFields();
      load();
      loadFunds();
    } catch (e) {
      message.error(e.response?.data?.message || 'Allocation failed');
    } finally {
      setAllocating(false);
    }
  };

  const columns = [
    { title: 'Ledger ID', dataIndex: 'ledgerId', key: 'ledgerId', width: 130 },
    { title: 'Case #', dataIndex: 'caseNumber', key: 'caseNumber', width: 120 },
    {
      title: 'Type', dataIndex: 'caseType', key: 'caseType', width: 100,
      render: (v) => <Tag color={CASE_TYPE_COLORS[v] || 'default'}>{v?.replace('_', ' ')}</Tag>,
    },
    {
      title: 'Purchase Cost', dataIndex: 'purchaseCost', key: 'purchaseCost', width: 130,
      render: (v) => <Text>{fmt(v)}</Text>,
    },
    {
      title: 'Smelter Revenue', dataIndex: 'smelterRevenue', key: 'smelterRevenue', width: 140,
      render: (v) => <Text>{fmt(v)}</Text>,
    },
    {
      title: 'Gross Profit', dataIndex: 'grossProfit', key: 'grossProfit', width: 130,
      render: (v) => (
        <Text strong style={{ color: v >= 0 ? '#3f8600' : '#cf1322' }}>
          {fmt(v)}
        </Text>
      ),
    },
    {
      title: 'Agent Commission', dataIndex: 'agentCommissionAmount', key: 'agentCommissionAmount', width: 140,
      render: (v) => (
        <Text style={{ color: v > 0 ? '#cf1322' : '#8c8c8c' }}>
          {v > 0 ? `(${fmt(v)})` : '—'}
        </Text>
      ),
    },
    {
      title: 'Net Profit', dataIndex: 'netProfit', key: 'netProfit', width: 130,
      render: (v) => (
        <Text strong style={{ color: v >= 0 ? '#3f8600' : '#cf1322' }}>
          {fmt(v)}
        </Text>
      ),
    },
    {
      title: 'Status', dataIndex: 'allocationStatus', key: 'allocationStatus', width: 110,
      render: (v) => <Tag color={STATUS_COLORS[v]}>{v}</Tag>,
    },
    {
      title: 'Revenue Fund', dataIndex: 'revenueFundId', key: 'revenueFundId', width: 140,
      render: (fund) => fund?.name ? <Tag color="cyan">{fund.name}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Allocated Fund', dataIndex: 'fundId', key: 'fundId', width: 130,
      render: (fund) => fund?.name || '—',
    },
    {
      title: 'Date', dataIndex: 'createdAt', key: 'createdAt', width: 120,
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Actions', key: 'actions', width: 120, fixed: 'right',
      render: (_, r) => (
        r.allocationStatus === 'PENDING' ? (
          <Button
            size="small"
            type="primary"
            ghost
            icon={<CheckCircleOutlined />}
            onClick={() => openAllocate(r)}
          >
            Allocate
          </Button>
        ) : (
          <Tag color="green">Done</Tag>
        )
      ),
    },
  ];

  return (
    <div>
      {/* Summary */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Total Gross Profit"
              value={fmt(summary.totalGrossProfit)}
              valueStyle={{ color: (summary.totalGrossProfit || 0) >= 0 ? '#3f8600' : '#cf1322', fontSize: 18 }}
              prefix={(summary.totalGrossProfit || 0) >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Less: Agent Commissions"
              value={fmt(summary.totalAgentCommissions)}
              valueStyle={{ color: (summary.totalAgentCommissions || 0) > 0 ? '#cf1322' : '#8c8c8c', fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Total Net Profit"
              value={fmt(summary.totalNetProfit)}
              valueStyle={{ color: (summary.totalNetProfit || 0) >= 0 ? '#3f8600' : '#cf1322', fontSize: 18 }}
              prefix={(summary.totalNetProfit || 0) >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Row gutter={8}>
              <Col span={12}>
                <Statistic title="Pending" value={summary.pendingCount || 0} valueStyle={{ fontSize: 16, color: '#faad14' }} />
              </Col>
              <Col span={12}>
                <Statistic title="Allocated" value={summary.allocatedCount || 0} valueStyle={{ fontSize: 16, color: '#52c41a' }} />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Toolbar */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space>
            <Select
              placeholder="All Case Types"
              allowClear
              style={{ width: 160 }}
              value={caseTypeFilter || undefined}
              onChange={(v) => { setPagination((p) => ({ ...p, current: 1 })); setCaseTypeFilter(v || ''); }}
            >
              <Select.Option value="WALK_IN">Walk-In</Select.Option>
              <Select.Option value="PLEDGE">Pledge</Select.Option>
            </Select>
            <Select
              placeholder="All Statuses"
              allowClear
              style={{ width: 160 }}
              value={statusFilter || undefined}
              onChange={(v) => { setPagination((p) => ({ ...p, current: 1 })); setStatusFilter(v || ''); }}
            >
              <Select.Option value="PENDING">Pending</Select.Option>
              <Select.Option value="ALLOCATED">Allocated</Select.Option>
            </Select>
            <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      {/* Table */}
      <DataTable persistId="accounting.case-pnl.main" serverPaginated
        dataSource={data}
        rowKey="_id"
        columns={columns}
        loading={loading}
        size="small"
        scroll={{ x: 1600 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          onChange: (p, s) => setPagination({ current: p, pageSize: s, total: pagination.total }),
        }}
      />

      {/* Allocate Modal */}
      <Modal
        title={`Allocate Case P/L — ${selectedEntry?.caseNumber || ''}`}
        open={allocateOpen}
        onCancel={() => setAllocateOpen(false)}
        onOk={() => allocateForm.submit()}
        confirmLoading={allocating}
        width={540}
      >
        {selectedEntry && (
          <div style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 6 }}>
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <Text strong>Case: </Text>
                <Text>{selectedEntry.caseNumber}</Text>
              </Col>
              <Col span={12}>
                <Text strong>Type: </Text>
                <Tag color={CASE_TYPE_COLORS[selectedEntry.caseType]}>
                  {selectedEntry.caseType?.replace('_', ' ')}
                </Tag>
              </Col>
              <Col span={12}>
                <Text strong>Smelter Revenue: </Text>
                <Text>{fmt(selectedEntry.smelterRevenue)}</Text>
              </Col>
              <Col span={12}>
                <Text strong>Gross Profit: </Text>
                <Text style={{ color: selectedEntry.grossProfit >= 0 ? '#3f8600' : '#cf1322' }}>
                  {fmt(selectedEntry.grossProfit)}
                </Text>
              </Col>
              <Col span={12}>
                <Text strong>Net Profit: </Text>
                <Text style={{ color: selectedEntry.netProfit >= 0 ? '#3f8600' : '#cf1322' }}>
                  {fmt(selectedEntry.netProfit)}
                </Text>
              </Col>
              {selectedEntry.revenueFundId?.name && (
                <Col span={12}>
                  <Text strong>Revenue Fund: </Text>
                  <Tag color="cyan">{selectedEntry.revenueFundId.name}</Tag>
                </Col>
              )}
            </Row>

            {/* Transfer info based on selected fund */}
            {selectedFundId && selectedEntry.revenueFundId && (
              <div style={{ marginTop: 12, padding: 8, background: '#f0f5ff', borderRadius: 4, border: '1px solid #d6e4ff' }}>
                {selectedEntry.netProfit >= 0 ? (
                  selectedFundId === selectedEntry.revenueFundId._id ? (
                    <Text type="secondary">
                      <SwapOutlined /> Revenue already in this fund. No transfer needed — will be marked as allocated.
                      Fund will be credited {fmt(selectedEntry.netProfit)}.
                    </Text>
                  ) : (
                    <Text style={{ color: '#1890ff' }}>
                      <SwapOutlined /> {fmt(selectedEntry.netProfit)} will be transferred from{' '}
                      <Text strong>{selectedEntry.revenueFundId.name}</Text> → target fund.
                    </Text>
                  )
                ) : (
                  <Text type="danger">
                    <SwapOutlined /> Loss of {fmt(Math.abs(selectedEntry.netProfit))} will be debited from the selected fund.
                  </Text>
                )}
              </div>
            )}

            {selectedEntry.netProfit < 0 && !selectedFundId && (
              <div style={{ marginTop: 8, color: '#cf1322' }}>
                <Text type="danger">This is a loss — allocating will DEBIT the selected fund.</Text>
              </div>
            )}
          </div>
        )}
        <Form form={allocateForm} layout="vertical" onFinish={handleAllocate}>
          <Form.Item name="fundId" label="Target Fund" rules={[{ required: true }]}>
            <Select
              placeholder="Choose fund"
              onChange={(v) => setSelectedFundId(v)}
            >
              {funds.map(fund => (
                <Select.Option key={fund._id} value={fund._id}>
                  {fund.name} ({fmt(fund.balance)})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
